import moment from "moment";

const isOpenShiftFlag = (payload) =>
  payload?.open_shift === true ||
  payload?.open_shift === 1 ||
  payload?.is_open_shift === true ||
  payload?.is_open_shift === 1 ||
  payload?.advance_open_shift === true ||
  payload?.advance_open_shift === 1;

/** First assigned worker id/uuid from basic or V5 advance create payload. */
export const resolveShiftRequestWorkerFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  const fromBasic = normalizeShiftRequestWorkerIdForApi(payload.worker_id);
  if (fromBasic != null) {
    return fromBasic;
  }
  if (!Array.isArray(payload.workers)) {
    return null;
  }
  for (const row of payload.workers) {
    if (!row || row.is_open) {
      continue;
    }
    const resolved = normalizeShiftRequestWorkerIdForApi(
      row.worker_id ?? row.worker?.id ?? row.worker?.uuid
    );
    if (resolved != null) {
      return resolved;
    }
  }
  return null;
};

/** True when create/update payload actually assigns a worker (not `[null]` / open shift). */
export const shiftRequestPayloadHasAssignedWorker = (payload) => {
  if (!payload || typeof payload !== "object") {
    return false;
  }

  if (Array.isArray(payload.workers) && payload.workers.length > 0) {
    return payload.workers.some(
      (row) =>
        row &&
        !row.is_open &&
        row.worker_id != null &&
        row.worker_id !== ""
    );
  }

  if (isOpenShiftFlag(payload)) {
    return false;
  }

  const raw = payload.worker_id;
  const candidates = Array.isArray(raw)
    ? raw
    : raw != null && raw !== ""
    ? [raw]
    : [];

  return candidates.some((entry) => {
    if (entry == null || entry === "") {
      return false;
    }
    if (typeof entry === "number" && !Number.isNaN(entry)) {
      return true;
    }
    if (typeof entry === "string" && entry.trim() !== "") {
      return true;
    }
    if (typeof entry === "object") {
      const id = entry.id ?? entry.uuid ?? entry.value;
      return id != null && id !== "";
    }
    return false;
  });
};

/** Shift create payload uses worker_id as number, uuid, option object, or [id]. */
export const normalizeShiftRequestWorkerIdForApi = (workerId) => {
  if (workerId == null || workerId === "") {
    return null;
  }
  const pick = (entry) => {
    if (entry == null || entry === "") {
      return null;
    }
    if (typeof entry === "number" && !Number.isNaN(entry)) {
      return entry;
    }
    if (typeof entry === "string" && entry.trim() !== "") {
      return entry.trim();
    }
    if (typeof entry === "object") {
      const id = entry.id ?? entry.uuid ?? entry.value;
      return id != null && id !== "" ? id : null;
    }
    return null;
  };

  if (Array.isArray(workerId)) {
    for (const entry of workerId) {
      const resolved = pick(entry);
      if (resolved != null) {
        return resolved;
      }
    }
    return null;
  }

  return pick(workerId);
};

const toHm = (val) => {
  if (val == null || val === "") return "";
  if (typeof val === "string" && val.includes("T")) {
    // Shift create payload uses convertTimeToDatetimeLocal: wall-clock HH:mm
    // encoded as ISO with a literal Z (not real UTC). Parse as UTC so we don't
    // apply the browser timezone and falsely diff start/end times on create.
    const m = moment.utc(val);
    return m.isValid() ? m.format("HH:mm") : "";
  }
  const dateTime = moment(
    val,
    ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"],
    true
  );
  if (dateTime.isValid()) {
    return dateTime.format("HH:mm");
  }
  const m = moment(val, ["HH:mm:ss", "HH:mm"], true);
  return m.isValid() ? m.format("HH:mm") : "";
};

const toDisplayDate = (val) => {
  if (val == null || val === "") return "";
  const m = moment(val);
  return m.isValid() ? m.format("DD/MM/YYYY") : String(val);
};

const participantNamesFromList = (list) => {
  const names = (list || [])
    .map((p) =>
      [p?.first_name, p?.last_name].filter(Boolean).join(" ").trim()
    )
    .filter(Boolean);
  return names.length ? names.join(", ") : "";
};

const requestCategoryMeta = (request) => {
  const id =
    request?.service_category_id ??
    request?.invoice_service_type?.id ??
    request?.invoiceServiceType?.id ??
    null;
  const name =
    request?.invoice_service_type?.name ??
    request?.invoiceServiceType?.name ??
    null;
  return { id: id != null ? String(id) : "", name: name || "" };
};

const payloadCategoryMeta = (payload, requestMeta) => {
  const id =
    payload?.invoice_service_type_id != null
      ? String(payload.invoice_service_type_id)
      : "";
  const name =
    payload?.invoice_service_type_name ||
    (id && id === requestMeta.id ? requestMeta.name : "") ||
    "";
  return { id, name };
};

const participantIdsFromPayload = (ptId, requestParticipants) => {
  const byUuid = new Map(
    (requestParticipants || [])
      .filter((p) => p?.uuid != null)
      .map((p) => [String(p.uuid), String(p.id)])
  );
  const byId = new Set(
    (requestParticipants || [])
      .filter((p) => p?.id != null)
      .map((p) => String(p.id))
  );

  const resolveOne = (raw) => {
    if (raw == null || raw === "") return null;
    if (typeof raw === "object") {
      if (raw.id != null) return String(raw.id);
      if (raw.uuid != null && byUuid.has(String(raw.uuid))) {
        return byUuid.get(String(raw.uuid));
      }
      return null;
    }
    const s = String(raw);
    if (/^\d+$/.test(s)) return s;
    if (byUuid.has(s)) return byUuid.get(s);
    if (byId.has(s)) return s;
    return null;
  };

  if (Array.isArray(ptId)) {
    return ptId.map(resolveOne).filter(Boolean).sort();
  }
  const one = resolveOne(ptId);
  return one ? [one] : [];
};

const participantNamesForIds = (ids, requestParticipants) => {
  const idSet = new Set(ids.map(String));
  const fromRequest = (requestParticipants || []).filter((p) =>
    idSet.has(String(p.id))
  );
  const names = participantNamesFromList(fromRequest);
  return names;
};

const addChange = (changes, field, oldVal, newVal) => {
  const oldS = oldVal === null || oldVal === undefined ? "" : String(oldVal).trim();
  const newS = newVal === null || newVal === undefined ? "" : String(newVal).trim();
  if (oldS === newS) return;
  changes[field] = {
    old: oldVal === "" ? null : oldVal,
    new: newVal === "" ? null : newVal,
  };
};

/**
 * Diff shift request vs New Shift dialog submit payload (only real edits).
 *
 * @returns {Record<string, { old: *, new: * }>}
 */
export const buildShiftRequestScheduleAuditChanges = (request, payload) => {
  if (!request || !payload || typeof payload !== "object") {
    return {};
  }

  const changes = {};
  const requestParticipants = Array.isArray(request?.participants)
    ? request.participants
    : [];

  addChange(
    changes,
    "shift_date",
    toDisplayDate(request.req_date),
    toDisplayDate(payload.shift_date)
  );
  addChange(
    changes,
    "start_time",
    toHm(request.start_time),
    toHm(payload.start_time)
  );
  addChange(
    changes,
    "end_time",
    toHm(request.end_time),
    toHm(payload.end_time)
  );

  const reqCat = requestCategoryMeta(request);
  const payCat = payloadCategoryMeta(payload, reqCat);
  if (reqCat.id !== payCat.id) {
    addChange(
      changes,
      "service_category",
      reqCat.name || reqCat.id || null,
      payCat.name || payCat.id || null
    );
  }

  const requestPtIds = requestParticipants
    .map((p) => String(p.id))
    .filter(Boolean)
    .sort();
  const payloadPtIds = participantIdsFromPayload(
    payload.pt_id,
    requestParticipants
  ).sort();

  if (requestPtIds.join(",") !== payloadPtIds.join(",")) {
    addChange(
      changes,
      "participant",
      participantNamesFromList(requestParticipants) || null,
      participantNamesForIds(payloadPtIds, requestParticipants) ||
        payloadPtIds.join(", ") ||
        null
    );
  }

  const workerNew = resolveShiftRequestWorkerFromPayload(payload);
  if (workerNew != null && workerNew !== "") {
    addChange(changes, "worker_id", null, workerNew);
  }

  return changes;
};

const toApiTime = (val) => {
  const hm = toHm(val);
  if (!hm) return undefined;
  return hm.length === 5 ? `${hm}:00` : hm;
};

/** Fields for sp/shift-requests/{uuid}/status so mobile list matches approved shift. */
export const buildShiftRequestSyncParamsFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return {};
  }

  const params = {};
  if (payload.shift_date != null && payload.shift_date !== "") {
    const m = moment(payload.shift_date);
    if (m.isValid()) {
      params.req_date = m.format("YYYY-MM-DD");
    }
  }

  const startTime = toApiTime(payload.start_time);
  const endTime = toApiTime(payload.end_time);
  if (startTime) params.start_time = startTime;
  if (endTime) params.end_time = endTime;

  if (
    payload.invoice_service_type_id != null &&
    payload.invoice_service_type_id !== ""
  ) {
    params.service_category_id = payload.invoice_service_type_id;
  }

  if (params.service_category_id == null && Array.isArray(payload.participant_splits)) {
    for (const group of payload.participant_splits) {
      const firstSplit = group?.splits?.[0];
      const serviceTypeId =
        firstSplit?.service_type_id ?? firstSplit?.invoice_service_type_id;
      if (serviceTypeId != null && serviceTypeId !== "") {
        params.service_category_id = serviceTypeId;
        break;
      }
    }
  }

  return params;
};
